// ─────────────────────────────────────────────────────────────────────────────
// Integrations tab — connect third-party tools. Slack is the one live
// integration: paste an Incoming Webhook URL to receive alert notifications in a
// channel. Wired to /settings/integrations via useIntegrations. Discrete inline
// actions (Connect / Send test / Disconnect) — not the header Save button.
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from "react";
import panes from "./panes.module.css";
import styles from "./Integrations.module.css";
import { IcoPlug } from "./icons";
import { SectionTitle, LoadingPane, ErrorBanner } from "./fields";
import { useIntegrations } from "../hooks/useIntegrations";

const TEST_LABEL = { idle: "Send test", sending: "Sending…", sent: "Sent ✓", failed: "Failed" };

export default function Integrations({ readOnly = false }) {
  const { slackConnected, loading, error, busy, testState, connect, disconnect, test } =
    useIntegrations();
  const [showForm, setShowForm] = useState(false);
  const [url, setUrl] = useState("");

  if (loading) return <LoadingPane/>;

  const onConnect = async (e) => {
    e.preventDefault();
    const ok = await connect(url.trim());
    if (ok) { setUrl(""); setShowForm(false); }
  };

  return (
    <div className={panes.pane}>
      <SectionTitle>Integrations</SectionTitle>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <div className={styles.list}>
        <div className={styles.card}>
          <div className={styles.row}>
            <div className={styles.icon}><IcoPlug/></div>

            <div className={styles.text}>
              <span className={styles.name}>Slack</span>
              <span className={styles.desc}>Alert notifications and incident updates</span>
            </div>

            <div className={styles.actions}>
              <span className={`${styles.status} ${slackConnected ? styles.statusOn : styles.statusOff}`}>
                <span className={styles.dot}/>
                {slackConnected ? "Connected" : "Not connected"}
              </span>

              {!readOnly && (slackConnected ? (
                <>
                  <button
                    className={styles.btn}
                    onClick={test}
                    disabled={testState === "sending"}
                  >
                    {TEST_LABEL[testState]}
                  </button>
                  <button
                    className={`${styles.btn} ${styles.btnDanger}`}
                    onClick={disconnect}
                    disabled={busy}
                  >
                    Disconnect
                  </button>
                </>
              ) : (
                <button className={styles.btn} onClick={() => setShowForm((s) => !s)}>
                  Connect
                </button>
              ))}
            </div>
          </div>

          {!readOnly && !slackConnected && showForm && (
            <form className={styles.connectForm} onSubmit={onConnect}>
              <input
                className={styles.input}
                type="text"
                placeholder="https://hooks.slack.com/services/…"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                autoFocus
              />
              <button
                className={`${styles.btn} ${styles.btnPrimary}`}
                type="submit"
                disabled={busy || !url.trim()}
              >
                {busy ? "Connecting…" : "Connect"}
              </button>
              <span className={styles.hint}>
                Create an Incoming Webhook in your Slack workspace and paste its URL here.
              </span>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
